import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input, Popconfirm, Popover, Select, Table, Tooltip, message } from "antd";
import { PlusOutlined, MoreOutlined, TagsOutlined, MailOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import * as api from "./controller";
import InviteCustomerModal from "./InviteCustomerModal";
import LeadTagPicker, { TagChips } from "./LeadTagPicker";
import LeadFormDrawer from "./LeadFormDrawer";
import LeadDetailDrawer, {
  LeadStatusPill, DormantPill, SOURCE_LABEL, principalOf, initialOf,
} from "./LeadDetailDrawer";
import BookingEmailModal from "./BookingEmailModal";
import "./crm.css";
import "./leads.css";

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "New", value: "new" },
  { label: "Invited", value: "invited" },
  { label: "Registered", value: "registered" },
  { label: "Converted", value: "converted" },
];

// Where a lead came from decides how much to trust its fields: a website form
// is the customer's own words, "by hand" is ours, "existing customer" was made
// after the fact to give an already-registered practice somewhere to hang tags.
const SOURCE_OPTIONS = [
  { label: "All sources", value: "" },
  { label: "Website form", value: "framer" },
  { label: "Entered by hand", value: "manual" },
  { label: "Existing customer", value: "backfill" },
  { label: "From email", value: "email" },
];

// The per-row Tags popover. Same shape as the drawer's: picker plus Save, so a
// typed new tag is created when the admin commits, not on the keystroke.
function RowTagsPopover({ lead, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ tagIds: [], newTags: [] });
  const [busy, setBusy] = useState(false);

  // Seed from the row when the popover opens — and only then, so a background
  // refetch of the table while it is open doesn't wipe a half-made selection.
  useEffect(() => {
    if (open) setDraft({ tagIds: (lead.tags || []).map((t) => t.id), newTags: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setBusy(true);
    const r = await api.setLeadTags(lead.id, draft);
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      onSaved(lead.id, r.json && Array.isArray(r.json.tags) ? r.json.tags : []);
      setOpen(false);
    } else {
      message.error((r.json && r.json.detail) || "Couldn't save the tags.");
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={
        <div className="crml-tag-pop" onClick={(e) => e.stopPropagation()}>
          <LeadTagPicker value={draft.tagIds} onChange={setDraft} size="small" />
          <div className="crml-tag-pop-foot">
            <Button size="small" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="small" type="primary" loading={busy} onClick={save}>Save</Button>
          </div>
        </div>
      }
    >
      <Tooltip title="Tags">
        <Button size="small" icon={<TagsOutlined />} />
      </Tooltip>
    </Popover>
  );
}

/**
 * The Leads tab of the Platform CRM — every prospect, from a website form
 * submission through invite and registration to a converted practice.
 *
 * Server-side paging and filtering (the practices table does the same); the
 * table reports its count up so the tab badge matches the toolbar. Row click
 * opens the detail drawer; the row actions are the three things done most
 * from the list — invite, send the booking email, tag — and the rest is
 * behind "more".
 */
export default function CrmLeads({ reloadTick, onCount, onOpenPractice, onOpenOffice }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [tagIds, setTagIds] = useState([]);
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const [formLead, setFormLead] = useState(null); // undefined/null = closed, {} = create, {id} = edit
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [inviteLead, setInviteLead] = useState(null);
  const [bookingLead, setBookingLead] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.getLeads({
        page,
        pageSize,
        search: search || undefined,
        tags: tagIds,
        status: status || undefined,
        source: source || undefined,
      });
      const ok = res.status >= 200 && res.status < 300 && res.json;
      setFailed(!ok);
      const list = ok && Array.isArray(res.json.results) ? res.json.results : [];
      const c = ok && typeof res.json.count === "number" ? res.json.count : list.length;
      setRows(list);
      setCount(c);
      if (onCount) onCount(c);
      if (!ok) message.error((res.json && res.json.detail) || "Couldn't load leads.");
    } catch (e) {
      setFailed(true);
      setRows([]);
      setCount(0);
      if (onCount) onCount(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, search, tagIds, status, source, onCount]);

  useEffect(() => {
    load();
  }, [load, reloadTick]);

  const openCreate = () => {
    setFormLead(null);
    setFormOpen(true);
  };
  const openEdit = (lead) => {
    setFormLead(lead);
    setFormOpen(true);
  };

  const remove = async (lead) => {
    setConfirmDeleteId(null);
    const r = await api.deleteLead(lead.id);
    if (r.status >= 200 && r.status < 300) {
      message.success(`${lead.practice_name || "Lead"} deleted.`);
      load({ silent: true });
    } else {
      message.error((r.json && r.json.detail) || "Couldn't delete the lead.");
    }
  };

  // Tags saved from a row popover: patch the row in place rather than refetch
  // the page — nothing else on the row changed.
  const rowTagsSaved = (leadId, tags) =>
    setRows((rs) => rs.map((r) => (r.id === leadId ? { ...r, tags } : r)));

  const filtersActive = !!(search || tagIds.length || status || source);

  const columns = useMemo(
    () => [
      {
        title: "Practice",
        render: (_, r) => {
          const sub = [r.city, r.pms].filter(Boolean).join(" · ");
          return (
            <div className="ftcd-who">
              <div className="ftcd-av">{initialOf(r.practice_name || r.label || r.email)}</div>
              <div className="ftcd-who-text">
                <div className="ftcd-name">{r.practice_name || r.label || r.email || "Unnamed lead"}</div>
                <div className="crm-sub">{sub || "—"}</div>
              </div>
            </div>
          );
        },
      },
      {
        title: "Principal contact",
        render: (_, r) => {
          const p = principalOf(r);
          if (p && (p.name || p.email || p.phone)) {
            const sub = [p.email, p.phone].filter(Boolean).join(" · ");
            return (
              <div className="crml-cell-sub">
                <div className="crm-cell-strong">{p.name || "—"}</div>
                <div className="crm-sub">{sub || "—"}</div>
              </div>
            );
          }
          // No named principal yet — the clinic's own address stands in.
          return r.email ? (
            <div className="crml-cell-sub">
              <div className="crm-cell-strong">{r.email}</div>
              <div className="crm-sub">{r.phone || "no named contact"}</div>
            </div>
          ) : (
            <span className="crml-dash">—</span>
          );
        },
      },
      {
        title: "Clinics",
        width: 90,
        align: "center",
        render: (_, r) =>
          r.is_multi_clinic ? (
            <span className="crml-num">{r.clinic_count || "2+"}</span>
          ) : (
            <span className="crm-sub">Single</span>
          ),
      },
      {
        title: "Tags",
        width: 200,
        render: (_, r) => <TagChips tags={r.tags} />,
      },
      {
        title: "Status",
        width: 150,
        render: (_, r) => (
          <span className="crml-pills">
            <LeadStatusPill status={r.status} />
            {r.is_dormant ? <DormantPill /> : null}
          </span>
        ),
      },
      {
        title: "Source",
        width: 130,
        render: (_, r) => <span className="crml-source">{SOURCE_LABEL[r.source] || r.source_label || r.source || "—"}</span>,
      },
      {
        title: "Booking email",
        width: 140,
        render: (_, r) =>
          r.booking_email_sent_at ? (
            <div className="crml-cell-sub">
              <div className="crml-num">{dayjs(r.booking_email_sent_at).format("MMM D, YYYY")}</div>
              {r.booking_email_sent_count > 1 ? <div className="crm-sub">sent {r.booking_email_sent_count} times</div> : null}
            </div>
          ) : (
            <span className="crml-dash">—</span>
          ),
      },
      {
        title: "Created",
        width: 120,
        render: (_, r) => <span className="crml-num">{r.created_at ? dayjs(r.created_at).format("MMM D, YYYY") : "—"}</span>,
      },
      {
        title: "",
        key: "actions",
        width: 230,
        align: "right",
        render: (_, r) => (
          <div className="crm-row-actions" onClick={(e) => e.stopPropagation()}>
            {r.status !== "converted" ? (
              <Button size="small" type="primary" ghost icon={<SendOutlined />} onClick={() => setInviteLead(r)}>
                Invite
              </Button>
            ) : null}
            <Tooltip title="Send booking email">
              <Button size="small" icon={<MailOutlined />} onClick={() => setBookingLead(r)} />
            </Tooltip>
            <RowTagsPopover lead={r} onSaved={rowTagsSaved} />
            <Popconfirm
              title="Delete this lead?"
              description="Its contacts, submissions and email history go with it."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              open={confirmDeleteId === r.id}
              onOpenChange={(o) => { if (!o) setConfirmDeleteId(null); }}
              onConfirm={() => remove(r)}
              onCancel={() => setConfirmDeleteId(null)}
            >
              {/* The span is the Popconfirm's anchor: Dropdown is a function
                  component with no DOM ref of its own, and the confirm is
                  opened programmatically from the menu, not by clicking. */}
              <span>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      { key: "edit", label: "Edit" },
                      { type: "divider" },
                      { key: "delete", label: "Delete", danger: true },
                    ],
                    onClick: ({ key }) => {
                      if (key === "edit") openEdit(r);
                      if (key === "delete") setConfirmDeleteId(r.id);
                    },
                  }}
                >
                  <Button size="small" icon={<MoreOutlined />} />
                </Dropdown>
              </span>
            </Popconfirm>
          </div>
        ),
      },
    ],
    // `remove` closes over `load`, which changes with the page and filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmDeleteId, load]
  );

  const emptyText = failed ? (
    <div className="crml-empty">
      <div className="crml-empty-title">Couldn't load leads.</div>
      <div className="crml-empty-hint">The server returned an error — this isn't the same as having none.</div>
      <Button size="small" style={{ marginTop: 10 }} onClick={() => load()}>Try again</Button>
    </div>
  ) : filtersActive ? (
    <div className="crml-empty">
      <div className="crml-empty-title">No leads match these filters.</div>
      <div className="crml-empty-hint">Clear a filter or two — a lead can only be in one status at a time.</div>
    </div>
  ) : (
    <div className="crml-empty">
      <div className="crml-empty-title">No leads yet.</div>
      <div className="crml-empty-hint">
        Add one with <b>New lead</b>, or connect the website form under CRM Settings › Leads and they will appear here on their own.
      </div>
    </div>
  );

  return (
    <>
      <div className="crm-toolbar">
        <Input.Search
          allowClear
          placeholder="Search practice, contact or email"
          style={{ maxWidth: 320 }}
          onSearch={(v) => {
            setPage(1);
            setSearch(v);
          }}
        />
        <LeadTagPicker
          allowCreate={false}
          value={tagIds}
          onChange={({ tagIds: next }) => {
            setPage(1);
            setTagIds(next || []);
          }}
          placeholder="Any tag"
          style={{ minWidth: 220, maxWidth: 360, width: "auto" }}
        />
        <Select
          style={{ minWidth: 160 }}
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setPage(1);
            setStatus(v);
          }}
        />
        <Select
          style={{ minWidth: 180 }}
          value={source}
          options={SOURCE_OPTIONS}
          onChange={(v) => {
            setPage(1);
            setSource(v);
          }}
        />
        <span className="crml-toolbar-spacer" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New lead
        </Button>
      </div>

      <div className="crm-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          rowClassName={() => "crm-row"}
          onRow={(r) => ({ onClick: () => setDetailId(r.id) })}
          locale={{ emptyText }}
          pagination={{
            current: page,
            pageSize,
            total: count,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </div>

      <LeadFormDrawer
        open={formOpen}
        lead={formLead}
        onClose={() => setFormOpen(false)}
        onSaved={() => load({ silent: true })}
      />
      <LeadDetailDrawer
        leadId={detailId}
        onClose={() => {
          setDetailId(null);
          // Tags, invites and the booking-email column all change inside the
          // drawer; refetch rather than mirror each one back into the row.
          load({ silent: true });
        }}
        onChanged={() => load({ silent: true })}
        onOpenPractice={onOpenPractice}
        onOpenOffice={onOpenOffice}
      />
      {inviteLead ? (
        <InviteCustomerModal
          open
          onClose={() => setInviteLead(null)}
          onCreated={() => load({ silent: true })}
          initial={{
            email: inviteLead.email || ((principalOf(inviteLead) || {}).email) || "",
            practiceName: inviteLead.practice_name || "",
            contactPersonName: (principalOf(inviteLead) || {}).name || "",
            contactPhone: inviteLead.phone || (principalOf(inviteLead) || {}).phone || "",
          }}
          leadId={inviteLead.id}
        />
      ) : null}
      {bookingLead ? (
        <BookingEmailModal
          lead={bookingLead}
          open
          onClose={() => setBookingLead(null)}
          onSent={() => load({ silent: true })}
        />
      ) : null}
    </>
  );
}
